/**
 * Périmètre du parc (onglet Localisation de la fiche) : ses zones sur une carte, à modifier à la main
 * (déplacer, ajouter ou retirer des sommets, ajouter ou supprimer une zone) ou à réimporter d'OSM
 * (aperçu en bleu avant de remplacer). Rien n'est enregistré avant « Enregistrer le périmètre » ;
 * juste après, « Annuler » remet l'ancien.
 * Un contour OSM compte souvent des milliers de sommets : au-delà de MAX_HANDLES on propose de simplifier
 * la zone (même curseur que l'import GPX) plutôt que d'afficher des milliers de poignées.
 * Avec `onPointChange`, la même carte place aussi le point d'accueil (enregistré avec la fiche) : une seule
 * carte pour la localisation, en plan ou en satellite.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Layer, Marker, Source, type MapLayerMouseEvent, type MapRef } from '@vis.gl/react-maplibre'
import { BaseMap } from '@web/features/map/components/BaseMap'
import { simplifyWithTolerance } from '@web/features/planning/utils/simplifyGpxPath'
import { fetchBoundary, previewBoundaryFromOsm, saveBoundary, type BoundaryData, type BoundaryZone, type LatLng } from '../api'
import { boundsOf, midpoint } from '../map/geo'
import { GPX_TOLERANCES } from './map/GpxImport'

const MAX_HANDLES = 300

/** Superficie approximative d'un anneau (m²), projection locale : assez précis pour un parc. */
function ringAreaM2(ring: LatLng[]): number {
  if (ring.length < 3) return 0
  const lat0 = (ring.reduce((s, p) => s + p.lat, 0) / ring.length) * (Math.PI / 180)
  const R = 6371000
  const xy = ring.map((p) => [((p.lng * Math.PI) / 180) * R * Math.cos(lat0), ((p.lat * Math.PI) / 180) * R])
  let a = 0
  for (let i = 0; i < xy.length; i++) {
    const [x1, y1] = xy[i]
    const [x2, y2] = xy[(i + 1) % xy.length]
    a += x1 * y2 - x2 * y1
  }
  return Math.abs(a) / 2
}

const zoneArea = (z: BoundaryZone) => Math.max(0, ringAreaM2(z.outer) - z.holes.reduce((s, h) => s + ringAreaM2(h), 0))
const formatArea = (m2: number) => (m2 >= 1e6 ? `${(m2 / 1e6).toLocaleString('fr-CA', { maximumFractionDigits: 2 })} km²` : `${Math.round(m2 / 1e4).toLocaleString('fr-CA')} ha`)
const ringCoords = (r: LatLng[]) => [...r.map((p) => [p.lng, p.lat]), [r[0].lng, r[0].lat]]
const featureOf = (zones: BoundaryZone[], selected: number | null): GeoJSON.FeatureCollection => ({
  type: 'FeatureCollection',
  features: zones
    .filter((z) => z.outer.length >= 3)
    .map((z, i) => ({
      type: 'Feature',
      properties: { selected: i === selected },
      geometry: { type: 'Polygon', coordinates: [ringCoords(z.outer), ...z.holes.filter((h) => h.length >= 3).map(ringCoords)] },
    })),
})
const allPoints = (zones: BoundaryZone[]) => zones.flatMap((z) => z.outer)

type BoundaryEditorProps = {
  orgId: number
  /** Point d'accueil de l'organisation (géré par le formulaire de la fiche). */
  point?: LatLng | null
  onPointChange?: (p: LatLng) => void
  pointDisabled?: boolean
}

export function BoundaryEditor({ orgId, point = null, onPointChange, pointDisabled = false }: BoundaryEditorProps) {
  const { t } = useTranslation('gestion')
  const [satellite, setSatellite] = useState(false)
  const [placing, setPlacing] = useState(false)
  const mapRef = useRef<MapRef>(null)
  const [saved, setSaved] = useState<BoundaryData | null>(null)
  const [zones, setZones] = useState<BoundaryZone[]>([])
  const [selected, setSelected] = useState<number | null>(null)
  const [vertex, setVertex] = useState<number | null>(null)
  const [drawing, setDrawing] = useState<LatLng[] | null>(null)
  const [preview, setPreview] = useState<BoundaryData | null>(null)
  const [osmRef, setOsmRef] = useState('')
  const [simplifyIndex, setSimplifyIndex] = useState(4)
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string; undo?: BoundaryZone[] } | null>(null)

  const fit = (pts: LatLng[]) => {
    const b = boundsOf(pts)
    if (b) mapRef.current?.fitBounds(b, { padding: 40, duration: 400, maxZoom: 15 })
  }

  useEffect(() => {
    const ctrl = new AbortController()
    fetchBoundary(orgId, ctrl.signal)
      .then((d) => {
        setSaved(d)
        setZones(d.zones)
        if (d.osm) setOsmRef(`${d.osm.type ?? 'relation'}/${d.osm.id}`)
      })
      .catch(() => undefined)
    return () => ctrl.abort()
  }, [orgId])

  // Cadrer sur le parc à la première réception du périmètre (la carte peut être prête avant ou après).
  const framed = useRef(false)
  useEffect(() => {
    if (framed.current || !saved || !mapRef.current) return
    framed.current = true
    fit([...allPoints(saved.zones), ...(point ? [point] : [])])
  })

  const dirty = saved != null && JSON.stringify(zones) !== JSON.stringify(saved.zones)
  const zone = selected != null ? zones[selected] : undefined
  const tooMany = zone != null && zone.outer.length > MAX_HANDLES
  const simplified = useMemo(
    () => (zone && tooMany ? simplifyWithTolerance(zone.outer.map((p) => [p.lng, p.lat]), GPX_TOLERANCES[simplifyIndex]).map(([lng, lat]) => ({ lat, lng })) : null),
    [zone, tooMany, simplifyIndex],
  )

  const updateZone = (i: number, outer: LatLng[]) => setZones((zs) => zs.map((z, k) => (k === i ? { ...z, outer } : z)))
  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setMessage(null)
    try {
      await fn()
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof Error ? e.message : String(e) })
    } finally {
      setBusy(false)
    }
  }

  const save = (next: BoundaryZone[], undo?: BoundaryZone[]) =>
    run(async () => {
      const d = await saveBoundary(orgId, next)
      setSaved(d)
      setZones(d.zones)
      setSelected(null)
      setVertex(null)
      setMessage({ kind: 'ok', text: t('perimetre.enregistre'), undo })
    })

  const onMapClick = (e: MapLayerMouseEvent) => {
    const p = { lat: e.lngLat.lat, lng: e.lngLat.lng }
    if (placing) {
      onPointChange?.(p)
      setPlacing(false)
      return
    }
    if (drawing) {
      setDrawing([...drawing, p])
      return
    }
    // Clic dans une zone : la choisir.
    const hit = e.features?.find((f) => f.layer.id === 'g-boundary-fill')
    if (hit) {
      // generateId : l'identifiant de l'entité est son rang, donc celui de la zone.
      const idx = Number(hit.id)
      setSelected(Number.isInteger(idx) && idx < zones.length ? idx : null)
      setVertex(null)
    }
  }

  const removeVertex = () => {
    if (selected == null || vertex == null || !zone || zone.outer.length <= 3) return
    updateZone(selected, zone.outer.filter((_, k) => k !== vertex))
    setVertex(null)
  }

  return (
    <section className="g-card g-boundary">
      <div className="g-boundary__head">
        <div>
          <h2 className="g-editor__section">{t('perimetre.titre')}</h2>
          <p className="g-muted">{t('perimetre.intro')}</p>
        </div>
        {zones.length > 0 && (
          <span className="g-muted">
            {t('perimetre.n_zones', { count: zones.length })} · {formatArea(zones.reduce((s, z) => s + zoneArea(z), 0))}
          </span>
        )}
      </div>

      <div className="g-boundary__layout">
        <div className="g-boundary__map">
          <BaseMap
            ref={mapRef}
            basemapContext="map"
            mapBackground={satellite ? 'satellite' : 'outdoor-bright'}
            constrainToRegion={false}
            initialViewState={{ longitude: -73.5, latitude: 46.5, zoom: 6 }}
            style={{ width: '100%', height: '100%' }}
            cursor={drawing || placing ? 'crosshair' : undefined}
            doubleClickZoom={!drawing}
            interactiveLayerIds={['g-boundary-fill']}
            onClick={onMapClick}
            onLoad={() => fit([...allPoints(saved?.zones ?? zones), ...(point ? [point] : [])])}
          >
            <Source id="g-boundary" type="geojson" data={featureOf(zones, selected)} generateId>
              <Layer id="g-boundary-fill" type="fill" paint={{ 'fill-color': '#954A00', 'fill-opacity': ['case', ['get', 'selected'], 0.22, 0.1] }} />
              <Layer id="g-boundary-line" type="line" paint={{ 'line-color': '#954A00', 'line-width': ['case', ['get', 'selected'], 3, 2] }} />
            </Source>
            {preview && (
              <Source id="g-boundary-preview" type="geojson" data={featureOf(preview.zones, null)}>
                <Layer id="g-boundary-preview-fill" type="fill" paint={{ 'fill-color': '#1565c0', 'fill-opacity': 0.12 }} />
                <Layer id="g-boundary-preview-line" type="line" paint={{ 'line-color': '#1565c0', 'line-width': 2.5, 'line-dasharray': [2, 1] }} />
              </Source>
            )}
            {simplified && (
              <Source id="g-boundary-simplified" type="geojson" data={{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: ringCoords(simplified) } }}>
                <Layer id="g-boundary-simplified" type="line" paint={{ 'line-color': '#e65100', 'line-width': 3 }} />
              </Source>
            )}
            {drawing && drawing.length > 0 && (
              <Source id="g-boundary-draft" type="geojson" data={{ type: 'Feature', properties: {}, geometry: { type: 'LineString', coordinates: drawing.map((p) => [p.lng, p.lat]) } }}>
                <Layer id="g-boundary-draft" type="line" paint={{ 'line-color': '#954A00', 'line-width': 3, 'line-dasharray': [2, 1] }} />
              </Source>
            )}
            {drawing?.map((p, i) => (
              <Marker key={`d-${i}`} longitude={p.lng} latitude={p.lat}>
                <span className="g-vertex g-vertex--active" />
              </Marker>
            ))}
            {zone &&
              !tooMany &&
              !drawing &&
              zone.outer.map((p, i) => (
                <Marker
                  key={`v-${selected}-${i}`}
                  longitude={p.lng}
                  latitude={p.lat}
                  draggable
                  onClick={(e) => {
                    e.originalEvent.stopPropagation()
                    setVertex(i)
                  }}
                  onDragEnd={(e) => updateZone(selected!, zone.outer.map((q, k) => (k === i ? { lat: e.lngLat.lat, lng: e.lngLat.lng } : q)))}
                >
                  <span className={`g-vertex${vertex === i ? ' g-vertex--active' : ''}`} />
                </Marker>
              ))}
            {zone &&
              !tooMany &&
              !drawing &&
              zone.outer.map((p, i) => {
                const m = midpoint(p, zone.outer[(i + 1) % zone.outer.length])
                return (
                  <Marker
                    key={`m-${selected}-${i}`}
                    longitude={m.lng}
                    latitude={m.lat}
                    onClick={(e) => {
                      e.originalEvent.stopPropagation()
                      const next = [...zone.outer]
                      next.splice(i + 1, 0, m)
                      updateZone(selected!, next)
                      setVertex(i + 1)
                    }}
                  >
                    <span className="g-midpoint" title={t('perimetre.ajouter_sommet')} />
                  </Marker>
                )
              })}
            {point && (
              <Marker
                longitude={point.lng}
                latitude={point.lat}
                anchor="bottom"
                draggable={!!onPointChange && !pointDisabled}
                onDragEnd={(e) => onPointChange?.({ lat: e.lngLat.lat, lng: e.lngLat.lng })}
              >
                <span className="g-boundary__home mdi mdi-map-marker" title={t('perimetre.accueil_titre')} />
              </Marker>
            )}
          </BaseMap>
          <button type="button" className="g-boundary__basemap" onClick={() => setSatellite((v) => !v)}>
            <span className={`mdi ${satellite ? 'mdi-terrain' : 'mdi-satellite-variant'}`} aria-hidden />
            <span>{satellite ? t('carte.topographique') : t('carte.satellite')}</span>
          </button>
        </div>

        <div className="g-boundary__side">
          {message && (
            <p className={`g-alert ${message.kind === 'error' ? 'g-alert--error' : 'g-alert--ok'}`}>
              {message.text}{' '}
              {message.undo && (
                <button type="button" className="g-link" disabled={busy} onClick={() => void save(message.undo!)}>
                  {t('commun.annuler')}
                </button>
              )}
            </p>
          )}

          {onPointChange && !drawing && (
            <div className="g-boundary__box">
              <strong>
                <span className="mdi mdi-map-marker" aria-hidden /> {t('perimetre.accueil_titre')}
              </strong>
              <p className="g-muted">
                {point ? t('perimetre.accueil_position', { lat: point.lat.toFixed(5), lng: point.lng.toFixed(5) }) : t('perimetre.accueil_manquant')}
              </p>
              <p className="g-hint">{t('perimetre.accueil_aide')}</p>
              <div className="g-editor__buttons">
                <button type="button" className={`btn${placing ? ' btn--primary' : ''}`} disabled={pointDisabled} onClick={() => setPlacing((v) => !v)}>
                  {placing ? t('perimetre.placer_accueil_actif') : t('perimetre.placer_accueil')}
                </button>
                {point && (
                  <button type="button" className="g-link" onClick={() => fit([point])}>
                    {t('perimetre.voir_accueil')}
                  </button>
                )}
              </div>
            </div>
          )}

          {drawing ? (
            <div className="g-boundary__box">
              <p>{t('perimetre.tracer_aide', { count: drawing.length })}</p>
              <div className="g-editor__buttons">
                <button
                  type="button"
                  className="btn btn--primary"
                  disabled={drawing.length < 3}
                  onClick={() => {
                    setZones((zs) => [...zs, { outer: drawing, holes: [] }])
                    setSelected(zones.length)
                    setDrawing(null)
                  }}
                >
                  {t('perimetre.terminer')}
                </button>
                <button type="button" className="btn" onClick={() => setDrawing(null)}>
                  {t('commun.annuler')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <h3 className="g-boundary__subtitle">{t('perimetre.zones')}</h3>
              {zones.length === 0 ? (
                <p className="g-hint">{t('perimetre.aucune_zone')}</p>
              ) : (
                <ul className="g-editor__list">
                  {zones.map((z, i) => (
                    <li key={i}>
                      <button
                        type="button"
                        className={`g-editor__item${selected === i ? ' g-editor__item--active' : ''}`}
                        onClick={() => {
                          setSelected(i)
                          setVertex(null)
                          fit(z.outer)
                        }}
                      >
                        <span className="g-editor__swatch" style={{ background: '#954A00' }} aria-hidden />
                        <span className="g-editor__item-text">
                          <span>{t('perimetre.zone_n', { n: i + 1 })}</span>
                          <span className="g-muted">
                            {formatArea(zoneArea(z))} · {t('perimetre.n_sommets', { count: z.outer.length })}
                            {z.holes.length > 0 && ` · ${t('perimetre.n_trous', { count: z.holes.length })}`}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" className="btn" onClick={() => (setSelected(null), setVertex(null), setDrawing([]))}>
                <span className="mdi mdi-vector-polygon" aria-hidden /> {t('perimetre.ajouter_zone')}
              </button>

              {zone && selected != null && (
                <div className="g-boundary__box">
                  <strong>{t('perimetre.zone_n', { n: selected + 1 })}</strong>
                  {tooMany ? (
                    <>
                      <p className="g-hint">{t('perimetre.trop_sommets', { count: zone.outer.length, max: MAX_HANDLES })}</p>
                      <label className="g-field">
                        <span className="g-field__label">
                          {t('carte.gpx_simplifier')} : <strong>{t('carte.gpx_ecart', { m: GPX_TOLERANCES[simplifyIndex] })}</strong>
                        </span>
                        <input type="range" min={1} max={GPX_TOLERANCES.length - 1} value={simplifyIndex} onChange={(e) => setSimplifyIndex(Number(e.target.value))} />
                      </label>
                      <p className="g-muted">{t('carte.gpx_points', { from: zone.outer.length, to: simplified?.length ?? 0 })}</p>
                      <button type="button" className="btn" disabled={!simplified || simplified.length < 3} onClick={() => simplified && updateZone(selected, simplified)}>
                        {t('perimetre.appliquer_simplification')}
                      </button>
                    </>
                  ) : (
                    <p className="g-hint">{t('perimetre.aide_sommets')}</p>
                  )}
                  <div className="g-editor__buttons">
                    {vertex != null && (
                      <button type="button" className="btn" disabled={zone.outer.length <= 3} onClick={removeVertex}>
                        {t('perimetre.retirer_sommet')}
                      </button>
                    )}
                    <span className="g-spacer" />
                    <button
                      type="button"
                      className="btn g-danger"
                      onClick={() => {
                        if (!window.confirm(t('perimetre.supprimer_zone_confirm', { n: selected + 1 }))) return
                        setZones((zs) => zs.filter((_, k) => k !== selected))
                        setSelected(null)
                        setVertex(null)
                      }}
                    >
                      <span className="mdi mdi-trash-can-outline" aria-hidden /> {t('perimetre.supprimer_zone')}
                    </button>
                  </div>
                </div>
              )}

              <div className="g-boundary__box">
                <strong>{t('perimetre.osm_titre')}</strong>
                <label className="g-field">
                  <span className="g-field__label">{t('perimetre.osm_ref')}</span>
                  <input
                    className="g-input"
                    value={osmRef}
                    placeholder="relation/1234567"
                    onChange={(e) => setOsmRef(e.target.value)}
                    onKeyDown={(e) => {
                      // Dans le formulaire de la fiche : Entrée ne doit pas l'enregistrer.
                      if (e.key === 'Enter') e.preventDefault()
                    }}
                  />
                </label>
                {preview ? (
                  <>
                    <p className="g-hint">
                      {t('perimetre.osm_apercu', { name: preview.osm?.name || osmRef, count: preview.zones.length, area: formatArea(preview.zones.reduce((s, z) => s + zoneArea(z), 0)) })}
                    </p>
                    <div className="g-editor__buttons">
                      <button
                        type="button"
                        className="btn btn--primary"
                        onClick={() => {
                          setZones(preview.zones)
                          setPreview(null)
                          setSelected(null)
                        }}
                      >
                        {t('perimetre.osm_remplacer')}
                      </button>
                      <button type="button" className="btn" onClick={() => setPreview(null)}>
                        {t('commun.annuler')}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const d = await previewBoundaryFromOsm(orgId, osmRef.trim())
                        setPreview(d)
                        fit(allPoints(d.zones))
                      })
                    }
                  >
                    <span className="mdi mdi-download-outline" aria-hidden /> {busy ? t('commun.chargement') : t('perimetre.osm_apercu_btn')}
                  </button>
                )}
              </div>

              <div className="g-editor__buttons">
                <button type="button" className="btn btn--primary" disabled={!dirty || busy} onClick={() => void save(zones, saved?.zones)}>
                  {busy ? t('commun.enregistrement') : t('perimetre.enregistrer')}
                </button>
                {dirty && (
                  <button type="button" className="btn" onClick={() => saved && (setZones(saved.zones), setSelected(null), setVertex(null))}>
                    {t('perimetre.abandonner')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
